import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Mic, 
  Square, 
  Pause, 
  Play,
  Upload,
  Loader2,
  AlertCircle,
  Lock,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const Waveform = ({ isRecording }) => {
  return (
    <div className="flex items-center justify-center gap-1 h-24">
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className={`w-2 rounded-full ${isRecording ? 'bg-red-500' : 'bg-stone-300'}`}
          animate={isRecording ? {
            height: [20, 60 + Math.random() * 40, 20],
          } : { height: 20 }}
          transition={{
            duration: 0.5 + Math.random() * 0.5,
            repeat: isRecording ? Infinity : 0,
            delay: i * 0.05
          }}
        />
      ))}
    </div>
  );
};

const NewMeeting = () => {
  const navigate = useNavigate();
  const { canCreateMeeting, getMeetingLimit, getMeetingsUsed, getPlanName, user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const userCanCreate = canCreateMeeting();
  const meetingLimit = getMeetingLimit();
  const meetingsUsed = getMeetingsUsed();
  const planName = getPlanName();
  const maxDuration = user?.subscription?.features?.max_duration_minutes || 20;

  // Cleanup audio URL when audioBlob changes
  useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setAudioUrl(null);
    }
  }, [audioBlob]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        setAudioBlob(blob);
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Error starting recording:", err);
      setError("No se pudo acceder al micrófono. Asegúrate de dar permiso.");
      toast.error("No se pudo acceder al micrófono");
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      const recording = recorder.state === "recording" || recorder.state === "paused";
      
      if (recording) {
        if (recorder.state === "paused") {
          recorder.resume();
          timerRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
          }, 1000);
          setIsPaused(false);
        } else {
          recorder.pause();
          clearInterval(timerRef.current);
          setIsPaused(true);
        }
      }
    }
  }, []); // No dependencies - uses refs

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      if (recorder.state === "recording" || recorder.state === "paused") {
        recorder.stop();
        streamRef.current?.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current);
        setIsRecording(false);
        setIsPaused(false);
      }
    }
  }, []); // No dependencies - uses refs

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['audio/webm', 'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/m4a', 'audio/ogg'];
      if (!allowedTypes.some(type => file.type.includes(type.split('/')[1]))) {
        toast.error("Formato de archivo no soportado. Usa MP3, WAV, WebM, MP4, M4A u OGG.");
        return;
      }
      setUploadedFile(file);
      setAudioBlob(null);
      toast.success(`Archivo "${file.name}" seleccionado`);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Por favor ingresa un título para la reunión");
      return;
    }
    
    const audioToUpload = audioBlob || uploadedFile;
    if (!audioToUpload) {
      toast.error("Por favor graba o sube un archivo de audio");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Get auth token
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("No estás autenticado. Por favor inicia sesión.");
        navigate("/login");
        return;
      }
      
      // Create meeting
      const meetingResponse = await axios.post(`${API}/meetings`, {
        title: title.trim(),
        description: description.trim() || null
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const meetingId = meetingResponse.data.id;
      
      // Upload audio
      const formData = new FormData();
      const fileName = uploadedFile ? uploadedFile.name : `recording-${Date.now()}.webm`;
      formData.append('file', audioToUpload, fileName);
      
      await axios.post(`${API}/meetings/${meetingId}/audio`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      toast.success("Reunión creada. Procesando audio...");
      navigate(`/meeting/${meetingId}`);
      
    } catch (err) {
      console.error("Error creating meeting:", err);
      setError(err.response?.data?.detail || "Error al crear la reunión");
      toast.error("Error al crear la reunión");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedFile(null);
    setRecordingTime(0);
  };

  const hasAudio = audioBlob || uploadedFile;

  // If user can't create more meetings, show limit alert
  if (!userCanCreate) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="w-12 h-12 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold mb-3">Límite alcanzado</h1>
          <p className="text-muted-foreground mb-2">
            Has utilizado {meetingsUsed} de {meetingLimit} reuniones este mes con tu plan {planName}.
          </p>
          <p className="text-muted-foreground mb-8">
            Actualiza tu plan para crear más reuniones.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/pricing">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600">
                <Sparkles className="w-5 h-5 mr-2" />
                Ver planes
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline">
                Volver al Dashboard
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-3">Nueva Reunión</h1>
          <p className="text-muted-foreground">
            Graba o sube el audio de tu reunión para generar la minuta automáticamente
          </p>
          <p className="text-sm text-orange-600 mt-2">
            {meetingLimit === -1 
              ? `Plan ${planName} - Reuniones ilimitadas • Máx. ${maxDuration} min por reunión`
              : `Plan ${planName} - ${meetingsUsed}/${meetingLimit} reuniones usadas • Máx. ${maxDuration} min por reunión`
            }
          </p>
        </div>

        {/* Meeting Info */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label htmlFor="title">Título de la reunión *</Label>
              <Input
                id="title"
                data-testid="meeting-title-input"
                placeholder="Ej: Reunión de planificación Q1"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Textarea
                id="description"
                data-testid="meeting-description-input"
                placeholder="Describe brevemente el tema de la reunión..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1.5 resize-none"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recording Section */}
        <Card className="overflow-hidden">
          <CardContent className="pt-6">
            {!hasAudio ? (
              <div
                className="text-center py-8"
              >
                  {/* Timer */}
                  <div className="mb-6">
                    <span 
                      className={`text-7xl font-bold mono tabular-nums ${isRecording ? 'text-red-500' : 'text-stone-300'}`}
                      data-testid="recording-timer"
                    >
                      {formatTime(recordingTime)}
                    </span>
                  </div>

                  {/* Waveform */}
                  <Waveform isRecording={isRecording && !isPaused} />

                  {/* Controls */}
                  <div className="flex items-center justify-center gap-4 mt-8">
                    {!isRecording ? (
                      <>
                        <Button
                          size="lg"
                          onClick={startRecording}
                          data-testid="start-recording-btn"
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full w-16 h-16"
                        >
                          <Mic className="w-6 h-6" />
                        </Button>
                        <div className="text-muted-foreground mx-4">o</div>
                        <label htmlFor="audio-upload">
                          <Button
                            variant="outline"
                            size="lg"
                            asChild
                            data-testid="upload-audio-btn"
                          >
                            <span className="cursor-pointer">
                              <Upload className="w-5 h-5 mr-2" />
                              Subir archivo
                            </span>
                          </Button>
                        </label>
                        <input
                          id="audio-upload"
                          type="file"
                          accept="audio/*"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          size="lg"
                          variant="outline"
                          onClick={pauseRecording}
                          data-testid="pause-recording-btn"
                          className="rounded-full w-14 h-14"
                        >
                          {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                        </Button>
                        <Button
                          size="lg"
                          onClick={stopRecording}
                          data-testid="stop-recording-btn"
                          className="bg-stone-900 hover:bg-stone-800 text-white rounded-full w-16 h-16"
                        >
                          <Square className="w-5 h-5" />
                        </Button>
                      </>
                    )}
                  </div>

                  {isRecording && (
                    <p className="text-sm text-muted-foreground mt-6">
                      {isPaused ? "Grabación pausada" : "Grabando... Habla claramente hacia el micrófono"}
                    </p>
                  )}
                </div>
              ) : (
                <div
                  className="text-center py-8"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Mic className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    {uploadedFile ? "Archivo listo" : "Grabación completada"}
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    {uploadedFile 
                      ? uploadedFile.name 
                      : `Duración: ${formatTime(recordingTime)}`
                    }
                  </p>
                  {audioUrl && (
                    <audio 
                      controls 
                      src={audioUrl} 
                      className="mx-auto mb-4"
                      data-testid="audio-preview"
                    />
                  )}
                  <Button
                    variant="outline"
                    onClick={resetRecording}
                    data-testid="reset-recording-btn"
                  >
                    Grabar de nuevo
                  </Button>
                </div>
              )}
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            {error}
          </div>
        )}

        {/* Submit Button */}
        <Button
          size="lg"
          onClick={handleSubmit}
          disabled={!title.trim() || !hasAudio || isSubmitting}
          data-testid="submit-meeting-btn"
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              Crear y Procesar Reunión
            </>
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          El audio será transcrito y analizado por IA para generar la minuta automáticamente.
        </p>
      </motion.div>
    </div>
  );
};

export default NewMeeting;
